import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import PageTitle from "./PageTitleCompoent";
import "../css/OrderDetailsScreen.css";
import { motion } from "framer-motion";
import api from "../api";

export default function OrderDetailsScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams();

  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!order);
  const [error, setError] = useState("");

  const handleBack = () => navigate(-1);

  // ✅ Fetch order if not passed via state (e.g., on refresh or direct link)
  useEffect(() => {
    if (!order) {
      const fetchOrder = async () => {
        try {
          setLoading(true);
          const token = localStorage.getItem("accessToken");
          const response = await api.get(`/admin/orders/${orderId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setOrder(response.data);
        } catch (err) {
          console.error("Failed to fetch order:", err);
          setError("Failed to load order details.");
        } finally {
          setLoading(false);
        }
      };
      fetchOrder();
    }
  }, [order, orderId]);

  if (loading) {
    return (
      <div className="order-details-container">
        <Sidebar />
        <div className="order-details-main">
          <Header />
          <div className="order-details-content">
            <PageTitle
              title="Order Details"
              subtitle="Detailed information about an Order"
              onBack={handleBack}
              showBackArrow={true}
            />
            <p>Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-details-container">
        <Sidebar />
        <div className="order-details-main">
          <Header />
          <div className="order-details-content">
            <PageTitle
              title="Order Details"
              subtitle="Detailed information about an Order"
              onBack={handleBack}
              showBackArrow={true}
            />
            <p>{error || "No order data found."}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-details-container">
      <Sidebar />
      <div className="order-details-main">
        <Header />
        <motion.div
          className="order-details-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <PageTitle
            title={`Order #${order.orderId}`}
            subtitle="Detailed information about the order"
            onBack={handleBack}
            showBackArrow={true}
          />

          <motion.div
            className="order-details-card"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {/* Order Header Section */}
            <div className="order-details-header">
              {/* Left side: Product Image */}
              <div className="product-info">
                <img
                  src={order.imageUrl}
                  alt={order.name}
                  className="product-image"
                />
              </div>

              {/* Right side: Details */}
              <div className="details-container">
                <div className="product-details">
                  <span className="product-name">{order.name}</span>
                  <span className="product-weight">
                    {order.weight} {order.weightUnit}
                  </span>
                </div>

                <div className="price-container">
                  <span className="price">₹{order.price * order.quantity}</span>
                  {order.originalPrice && (
                    <span className="original-price">₹{order.originalPrice}</span>
                  )}
                </div>

                <div className="order-date">
                  <span>{order.date}</span>
                </div>

                <div className="status-container">
                  <span className="label">Status:</span>
                  <span
                    className={`order-status-badge ${order.status.toLowerCase()}`}
                  >
                    {order.status}
                  </span>
                </div>

                <div className="payment-info">
                  <span className="label">Payment:</span>
                  <span>{order.payment}</span>
                </div>

                <div className="payment-type-info">
                  <span className="label">Payment Type:</span>
                  <span>{order.paymentType}</span>
                </div>
              </div>
            </div>

            {/* Description Section */}
            <div className="description-section">
              <h3 className="description-title">{order.name}</h3>
              <p className="description-text">{order.description}</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
